import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import api, { formatApiErrorDetail } from "../../lib/api";
import { PageHeader } from "../../components/StaffLayout";
import StatCard from "../../components/StatCard";
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  PieChart,
  Pie,
  Cell,
} from "recharts";
import { Plus, ArrowRight, Wallet, Stethoscope } from "@phosphor-icons/react";

const STATUS_COLORS = { new: "#3B82F6", contacted: "#EAB308", converted: "#16A34A", lost: "#DC2626" };

export default function Dashboard() {
  const [stats, setStats] = useState(null);
  const [reminders, setReminders] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    let mounted = true;
    Promise.all([api.get("/dashboard/stats"), api.get("/reminders")])
      .then(([statsRes, remindersRes]) => {
        if (!mounted) return;
        setStats(statsRes.data);
        setReminders(remindersRes.data);
        setError("");
      })
      .catch((err) => {
        if (!mounted) return;
        setError(formatApiErrorDetail(err.response?.data?.detail));
      });

    return () => {
      mounted = false;
    };
  }, []);

  const pie = useMemo(
    () =>
      stats
        ? Object.entries(stats.enquiries_by_status || {}).map(([name, value]) => ({ name, value }))
        : [],
    [stats]
  );

  const opd = stats?.today_opd || {
    total: 0,
    scheduled: 0,
    completed: 0,
    cancelled: 0,
    no_show: 0,
  };
  const revenue = stats?.revenue || {
    total_paid: 0,
    total_billed: 0,
    outstanding: 0,
    collected_today: 0,
    paid_month: 0,
    billed_month: 0,
  };

  return (
    <div data-testid="staff-dashboard">
      <PageHeader
        title="Operations Dashboard"
        subtitle="Today's snapshot of leads, OPD, appointments, and revenue"
        actions={
          <Link
            to="/staff/enquiries"
            className="inline-flex items-center gap-2 bg-[#E60000] hover:bg-[#CC0000] text-white font-semibold rounded-sm px-4 py-2 text-sm"
            data-testid="dashboard-add-enquiry"
          >
            <Plus size={16} weight="bold" /> New enquiry
          </Link>
        }
      />
      <div className="px-6 sm:px-10 py-8 space-y-8">
        {error ? (
          <div className="bg-red-50 border border-red-200 rounded-sm p-4 text-sm text-red-700">
            {error}
          </div>
        ) : null}

        <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-4">
          <StatCard label="Total Patients" value={stats?.total_patients ?? "-"} to="/staff/patients" />
          <StatCard label="Total Enquiries" value={stats?.total_enquiries ?? "-"} to="/staff/enquiries" />
          <StatCard label="New (open)" value={stats?.new_enquiries ?? "-"} accent sub="Needs first contact" to="/staff/enquiries" />
          <StatCard label="Converted" value={stats?.converted_enquiries ?? "-"} sub={`${stats?.conversion_rate ?? 0}% rate`} to="/staff/enquiries" />
          <StatCard label="Today's Appts" value={stats?.today_appointments ?? "-"} to="/staff/appointments" />
          <StatCard
            label="Revenue Rs"
            value={formatCompactCurrency(revenue.total_paid)}
            sub={`Rs ${formatCompactCurrency(revenue.outstanding)} outstanding`}
            to="/staff/invoices"
          />
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          <div className="lg:col-span-2 bg-white border border-zinc-200 rounded-sm p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-bold tracking-tight" style={{ fontFamily: "Manrope, sans-serif" }}>
                Enquiries - Last 7 days
              </h3>
              <span className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">Daily volume</span>
            </div>
            <div className="h-64" style={{ minHeight: 280 }}>
              <ResponsiveContainer>
                <BarChart data={stats?.weekly_enquiries || []}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                  <XAxis dataKey="date" tick={{ fontSize: 11 }} />
                  <YAxis allowDecimals={false} tick={{ fontSize: 11 }} />
                  <Tooltip />
                  <Bar dataKey="count" fill="#E60000" radius={[2, 2, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>

          <div className="bg-white border border-zinc-200 rounded-sm p-6">
            <h3 className="text-lg font-bold tracking-tight mb-4" style={{ fontFamily: "Manrope, sans-serif" }}>
              Lead funnel
            </h3>
            <div className="h-64" style={{ minHeight: 280 }}>
              <ResponsiveContainer>
                <PieChart>
                  <Pie data={pie} dataKey="value" innerRadius={50} outerRadius={90} stroke="none">
                    {pie.map((item, index) => (
                      <Cell key={index} fill={STATUS_COLORS[item.name] || "#999"} />
                    ))}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
            </div>
            <div className="flex flex-wrap gap-3 text-xs">
              {pie.map((item) => (
                <div key={item.name} className="flex items-center gap-1.5">
                  <span className="inline-block w-2.5 h-2.5 rounded-sm" style={{ background: STATUS_COLORS[item.name] }} />
                  <span className="uppercase tracking-wider font-bold text-zinc-700">{item.name}</span>
                  <span className="text-zinc-500">{item.value}</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <Panel
            title="Today's OPD"
            icon={<Stethoscope size={16} weight="bold" />}
            action={
              <Link to="/staff/appointments" className="text-xs uppercase tracking-[0.2em] font-bold text-zinc-700 hover:text-[#E60000]">
                Open appointments
              </Link>
            }
          >
            <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
              <MiniMetric label="Total" value={opd.total} />
              <MiniMetric label="Scheduled" value={opd.scheduled} />
              <MiniMetric label="Completed" value={opd.completed} />
              <MiniMetric label="Cancelled" value={opd.cancelled} />
              <MiniMetric label="No-show" value={opd.no_show} />
            </div>

            <div className="mt-5 border-t border-zinc-100 pt-4 space-y-2">
              {(stats?.today_schedule || []).length === 0 ? (
                <div className="text-sm text-zinc-500">No OPD appointments lined up for today.</div>
              ) : (
                (stats?.today_schedule || []).map((item) => (
                  <div key={item.id} className="flex items-center justify-between gap-3 text-sm">
                    <div className="min-w-0">
                      <div className="font-semibold text-zinc-900">{item.patient_name || "Patient"}</div>
                      <div className="text-xs text-zinc-500">
                        {new Date(item.starts_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
                        {" · "}
                        {item.treatment || "Consultation"}
                        {item.doctor_name ? ` · ${item.doctor_name}` : " · Unassigned"}
                      </div>
                    </div>
                    <span className="text-[10px] uppercase tracking-[0.2em] font-bold text-zinc-600">{item.status}</span>
                  </div>
                ))
              )}
            </div>
          </Panel>

          <Panel
            title="Revenue Snapshot"
            icon={<Wallet size={16} weight="bold" />}
            action={
              <Link to="/staff/invoices" className="text-xs uppercase tracking-[0.2em] font-bold text-zinc-700 hover:text-[#E60000]">
                Open invoices
              </Link>
            }
          >
            <div className="grid grid-cols-2 gap-3">
              <RevenueMetric label="Collected today" value={revenue.collected_today} accent />
              <RevenueMetric label="This month paid" value={revenue.paid_month} />
              <RevenueMetric label="This month billed" value={revenue.billed_month} />
              <RevenueMetric label="Outstanding" value={revenue.outstanding} warn />
            </div>
          </Panel>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <ReminderList title="Today's follow-ups" items={reminders?.today_follow_ups} empty="Nothing on the schedule today" />
          <ReminderList title="Overdue follow-ups" items={reminders?.overdue_follow_ups} accent empty="Nothing overdue" />
        </div>
      </div>
    </div>
  );
}

function Panel({ title, icon, action, children }) {
  return (
    <div className="bg-white border border-zinc-200 rounded-sm p-5">
      <div className="flex items-center justify-between gap-3 mb-4">
        <div className="flex items-center gap-2">
          <span className="text-zinc-700">{icon}</span>
          <h3 className="text-lg font-bold tracking-tight" style={{ fontFamily: "Manrope, sans-serif" }}>
            {title}
          </h3>
        </div>
        {action}
      </div>
      {children}
    </div>
  );
}

function MiniMetric({ label, value }) {
  return (
    <div className="border border-zinc-200 rounded-sm p-3">
      <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">{label}</div>
      <div className="mt-2 text-2xl font-black tracking-tighter text-zinc-900" style={{ fontFamily: "Manrope, sans-serif" }}>
        {value}
      </div>
    </div>
  );
}

function RevenueMetric({ label, value, accent, warn }) {
  return (
    <div className="border border-zinc-200 rounded-sm p-3">
      <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">{label}</div>
      <div
        className={`mt-2 text-2xl font-black tracking-tighter ${warn ? "text-[#E60000]" : accent ? "text-green-700" : "text-zinc-900"}`}
        style={{ fontFamily: "Manrope, sans-serif" }}
      >
        Rs {formatCompactCurrency(value)}
      </div>
    </div>
  );
}

function ReminderList({ title, items, empty, accent }) {
  return (
    <div className="bg-white border border-zinc-200 rounded-sm">
      <div className={`px-5 py-4 border-b border-zinc-200 flex items-center justify-between ${accent ? "bg-red-50" : ""}`}>
        <h3 className="font-bold tracking-tight" style={{ fontFamily: "Manrope, sans-serif" }}>{title}</h3>
        <Link to="/staff/reminders" className="text-xs uppercase tracking-[0.2em] font-bold text-zinc-700 hover:text-[#E60000] inline-flex items-center gap-1">
          View all <ArrowRight size={12} weight="bold" />
        </Link>
      </div>
      <div className="divide-y divide-zinc-100">
        {(items || []).length === 0 ? (
          <div className="p-6 text-sm text-zinc-500">{empty}</div>
        ) : (
          items.map((item) => (
            <div key={item.id} className="px-5 py-3 flex items-center justify-between text-sm">
              <div>
                <div className="font-semibold text-zinc-900">{item.name}</div>
                <div className="text-zinc-500 text-xs">{item.phone} · {item.service || "General"}</div>
              </div>
              <span className="text-[10px] uppercase tracking-[0.2em] font-bold text-zinc-600">{item.status}</span>
            </div>
          ))
        )}
      </div>
    </div>
  );
}

function formatCompactCurrency(value) {
  const amount = Number(value || 0);
  return amount.toLocaleString("en-IN", { maximumFractionDigits: 0 });
}
